// How the site reads the API: each answer kept for a few minutes and shared
// by everything that asks for it meanwhile, and what a failed request says.
// The network and the clock are handed in (createApi), so this runs (and is
// tested, ../api-client.test.ts) without a browser; state.ts makes the
// site's own.

// The API sits behind a per-IP rate limit (20 requests / 10s on /api/*), so reuse
// responses for a few minutes instead of refetching on every range click.
const CACHE_MS = 5 * 60 * 1000;

export const LOAD_ERROR =
  "Couldn't load data. If you've been clicking quickly, wait a few seconds and try again.";

// A response from the API that wasn't a 200, with its status.
export class HttpError extends Error {
  readonly status: number;

  constructor(status: number) {
    super(`HTTP ${status}`);
    this.status = status;
  }
}

export const isNotFound = (err: unknown): boolean => err instanceof HttpError && err.status === 404;

export function errorMessage(err: unknown): string {
  return isNotFound(err) ? "That game isn't tracked." : LOAD_ERROR;
}

// What createApi needs of fetch's answer.
export interface ApiResponse {
  ok: boolean;
  status: number;
  json(): Promise<unknown>;
}

// The API's answer at a path, as its type T (../../shared/api.ts), taken on
// trust: steam-api writes it from the same types.
export type Api = <T>(path: string) => Promise<T>;

// An API reader for the API at `base`, asking with `fetch` and timing its
// cache by `now` (unix ms). `known` is answers it starts with, keyed by
// their paths: those a page the Worker wrote arrives with (see
// ../src/index.ts), so the first render doesn't ask for them again.
export function createApi(
  base: string,
  fetch: (url: string) => Promise<ApiResponse>,
  now: () => number,
  known: Readonly<Record<string, unknown>> = {}
): Api {
  const responses = new Map<string, { at: number; promise: Promise<unknown> }>();

  for (const [path, body] of Object.entries(known)) {
    responses.set(path, { at: now(), promise: Promise.resolve(body) });
  }

  return <T>(path: string): Promise<T> => {
    const cached = responses.get(path);

    if (cached && now() - cached.at < CACHE_MS) {
      return cached.promise as Promise<T>;
    }

    const promise = fetch(base + path).then((res) => {
      if (!res.ok) {
        throw new HttpError(res.status);
      }
      return res.json() as Promise<T>;
    });

    responses.set(path, { at: now(), promise });

    // A failure isn't kept: the next ask tries again.
    promise.catch(() => {
      if (responses.get(path)?.promise === promise) responses.delete(path);
    });

    return promise;
  };
}
